import { askText } from './ui-dialog.js';
import { changeStudentPin, loginCreator, loginStudent, logout, observeSession, getAccountType } from './auth.js';
import { clearDriveAuthorization } from './drive-authoring.js';
import { renderUserMenu } from './user-menu.js';

const app = document.getElementById('app');
const sessionActions = document.getElementById('sessionActions');
const appHeader = document.querySelector('.app-header');
const headerResize = new ResizeObserver(() => {
  document.documentElement.style.setProperty('--app-header-height', `${appHeader.getBoundingClientRect().height}px`);
});
headerResize.observe(appHeader);
let activeStudentApp = null;
let activeCreatorApp = null;
let currentSessionUser = null;
let sessionRevision = 0;
let goingHome = false;
document.getElementById('brandHome').addEventListener('click', async event => {
  event.preventDefault();
  if (goingHome) return;
  goingHome = true;
  try {
    const controller = activeStudentApp || activeCreatorApp;
    if (controller) {
      if (await controller.goHome() === false) return;
    } else if (!currentSessionUser) renderLogin();
    else return;
    window.scrollTo({top:0,behavior:'instant'});
    const heading=app.querySelector('h2');
    if(heading){heading.setAttribute('tabindex','-1');heading.focus({preventScroll:true});}
  } catch { alert('Chưa thể về Trang chủ. Hãy thử lại; màn hình hiện tại được giữ để tránh mất bài đang làm.'); }
  finally { goingHome = false; }
});

document.addEventListener('question-banks:creator-role-change', event => {
  if (currentSessionUser && activeCreatorApp) renderSession(currentSessionUser, 'creator', event.detail);
});

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);
}

function safeErrorCode(error) {
  const code = String(error?.code || error?.name || 'unknown_error');
  return /^[A-Za-z0-9_./-]{1,100}$/.test(code) ? code : 'unknown_error';
}

function safeErrorMessage(error) {
  return String(error?.message || 'Không có chi tiết')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/https?:\/\/\S+/g, '[url]')
    .slice(0, 240);
}

function renderSession(user, accountType = null, creatorState = null) {
  if (!user) {
    renderUserMenu(sessionActions, { user: null });
    return;
  }
  renderUserMenu(sessionActions, {
    user, accountType, creatorState,
    onStudentSettings: () => activeStudentApp?.openSettings(),
    onAddRole: async () => {
      try {
        const nextState = await activeCreatorApp?.addRole();
        if (nextState) renderSession(user, 'creator', nextState);
      } catch (error) {
        alert(error.message || 'Không thể thêm vai trò. Vui lòng thử lại.');
      }
    },
    onChangePin: async () => {
      const pin = await askText('Nhập PIN mới gồm đúng 6 chữ số:');
      if (pin == null) return;
      try { await changeStudentPin(user, pin); alert('Đã đổi PIN.'); }
      catch (error) { alert(error.message || 'Không thể đổi PIN. Hãy đăng nhập lại rồi thử lại.'); }
    },
    onSwitchRole: role => {
      const nextState = activeCreatorApp?.switchRole(role);
      if (nextState) renderSession(user, 'creator', nextState);
    },
    onLogout: () => {
      clearDriveAuthorization();
      logout();
    }
  });
}

function renderLogin() {
  const template = document.getElementById('loginTemplate');
  app.replaceChildren(template.content.cloneNode(true));
  const studentForm = document.getElementById('studentLoginForm');
  studentForm.addEventListener('submit', async event => {
    event.preventDefault();
    const button = studentForm.querySelector('button');
    const message = studentForm.querySelector('[data-message]');
    const form = new FormData(studentForm);
    button.disabled = true;
    message.textContent = '';
    try {
      await loginStudent(form.get('handle'), form.get('pin'));
    } catch (error) {
      message.textContent = error.message;
      button.disabled = false;
    }
  });
  const creatorButton = document.getElementById('creatorLoginButton');
  creatorButton.addEventListener('click', async () => {
    const message = app.querySelector('[data-creator-message]');
    creatorButton.disabled = true;
    message.textContent = '';
    try {
      await loginCreator();
    } catch (error) {
      message.textContent = `Không thể đăng nhập Google (${safeErrorCode(error)}). Vui lòng thử lại.`;
      creatorButton.disabled = false;
    }
  });
}

observeSession(async user => {
  const revision = ++sessionRevision;
  currentSessionUser = user;
  activeStudentApp?.stop();
  activeStudentApp = null;
  activeCreatorApp?.stop();
  activeCreatorApp = null;
  renderSession(user);
  if (!user) {
    renderLogin();
    return;
  }
  app.innerHTML = '<section class="panel">Đang xác định loại tài khoản…</section>';
  const mount = document.createElement('div');
  try {
    const accountType = await getAccountType(user);
    if (revision !== sessionRevision) return;
    app.replaceChildren(mount);
    if (accountType === 'student') {
      renderSession(user, 'student');
      const { createStudentApp } = await import('./student-app.js');
      if (revision !== sessionRevision) return;
      activeStudentApp = createStudentApp(mount, user);
      await activeStudentApp.start();
    } else {
      const { createCreatorApp } = await import('./creator-app.js');
      if (revision !== sessionRevision) return;
      activeCreatorApp = createCreatorApp(mount, user);
      await activeCreatorApp.start();
      if (revision !== sessionRevision) return;
      renderSession(user, 'creator', activeCreatorApp.getUserMenuState());
    }
  } catch (error) {
    if (revision !== sessionRevision) return;
    if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk .+ failed/i.test(String(error?.message || ''))) {
      app.innerHTML = '<section class="panel"><h2>Không tải được màn hình</h2><p>Một tệp của ứng dụng chưa tải được. Điều này có thể xảy ra khi ứng dụng vừa cập nhật hoặc kết nối bị gián đoạn.</p><button type="button" data-reload-app><svg class="action-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" style="vertical-align:middle;pointer-events:none;flex-shrink:0"><path d="M20 4v6h-6M4 20v-6h6M6 7a7 7 0 0 1 12-1l2 4M4 14l2 4a7 7 0 0 0 12-1"/></svg> Tải lại trang</button><p>Nếu vẫn gặp lỗi, hãy kiểm tra kết nối rồi thử lại.</p></section>';
      app.querySelector('[data-reload-app]').addEventListener('click', () => window.location.reload());
      console.error('Không tải được mô-đun giao diện:', safeErrorMessage(error));
      return;
    }
    let hasGoogleIdentity = false;
    try {
      const token = await user.getIdTokenResult(true);
      hasGoogleIdentity = Array.isArray(token.claims.firebase?.identities?.['google.com']);
    } catch { /* Chỉ dùng để chẩn đoán trạng thái, không thay đổi phiên. */ }
    if (revision !== sessionRevision) return;
    const code = safeErrorCode(error);
    const detail = safeErrorMessage(error);
    console.error('Không thể khởi tạo phiên:', code, detail);
    app.innerHTML = `<section class="panel"><h2>Không thể mở phiên</h2><p>Mã lỗi: <code>${escapeHtml(code)}</code></p><p>Chi tiết: <code>${escapeHtml(detail)}</code></p><p>Google identity trong Firebase token: <strong>${hasGoogleIdentity ? 'có' : 'không'}</strong>.</p><p>Hãy gửi các giá trị này cho System Admin; không gửi token hoặc thông tin đăng nhập.</p></section>`;
  }
});
import '../../question_renderer_shared.css';
